import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-overlay>` says about itself.
 *
 * The thing worth saying twice is that this is ONE shell rather than a modal somebody extended into
 * a drawer. The scrim, the placement, the panel edge, the focus trap and the close request are the
 * same code in both cases; `placement` is the only difference, and the width comes from a token the
 * host sets. So there are two examples that differ in exactly that one input, because a reader who
 * sees the same declaration produce a centred dialog and a right-hand drawer has learned the design
 * — where a reader shown only a modal goes and writes a second component for the drawer.
 *
 * ON THE CONTENT OF THE EXAMPLES. The shell deliberately carries NO padding of its own: it owns the
 * scrim, the placement and the edge, and the host owns everything inside. Every real caller brings
 * its own padded head with a full-bleed divider under it, which only works because the shell does
 * not pad. An example that drops bare children straight in gets its text sheared by the panel edge
 * — that happened, and the fix was in the example rather than in the shell, because padding the
 * shell would inset the divider in all three real callers to make one preview look right. So the
 * examples bring padded content the way a screen does. They bring it as `<caos-inspector>`, which
 * is the library's own padded head + divider + body, and which the source header names as the piece
 * this one shares its house style with — the inspector is the panel, and the scrim and the
 * placement are the half it does not own. The examples use it plain, as a caller's content. An
 * inspector asked for `modal` gets that half by ending up INSIDE one of these (CAOS-429), so it
 * is never nested in an example here: that would be an overlay in an overlay.
 *
 * ON THE HOST BLOCK. Two things here genuinely belong to the surrounding page and neither can be
 * expressed as a stored value: presence in the DOM IS open, so something has to unmount the overlay
 * when it reports a close, and the panel width is a token the caller sets (the record create form
 * sets `min(820px, 92vw)`; the package picker takes the default). Left unhosted, clicking the scrim
 * would do nothing, which is a working example teaching that the piece does nothing.
 */
const docs: ComponentDocs = {
  aliases: ['modal', 'dialog', 'drawer', 'sheet', 'side panel', 'scrim', 'popup', 'lightbox'],

  answers: [
    'put a form in front of somebody without leaving the page',
    'slide a detail panel in from the right-hand side',
    'dim the page behind a dialog so it reads as the only thing in play',
    'keep the keyboard inside a dialog and hand focus back when it closes',
    'ask for a confirmation before something destructive',
  ],

  // Declared as NOWHERE, and the reason is the piece's own contract: presence in the DOM is what
  // "open" means. An overlay dropped into a page layout would be an overlay that is open from the
  // moment the page draws, and shut only once somebody closed it. It is mounted by the surface that
  // opens it — a record action, a list-view control, an empty-workspace call to action — never
  // placed as an item on one.
  placements: [],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The accessible name of the dialog. It is what a screen reader announces on entering the ' +
        'panel, so it should say which dialog this is rather than what it contains.',
    },
    {
      name: 'placement',
      attribute: 'placement',
      type: 'text',
      options: ['center', 'end'],
      description:
        'Where the panel sits: center, a modal floating near the top of the page, or end, a ' +
        'full-height drawer flush against the right-hand edge. Everything else about the two is ' +
        'identical.',
      default: 'center',
    },
  ],

  events: [
    {
      name: 'caos-overlay-close',
      when:
        'The scrim is clicked or Escape is pressed. It is a REQUEST — the overlay does not remove ' +
        'itself, and the event is cancelable, so a host with unsaved changes can prevent it and ask.',
      carries: 'nothing',
    },
  ],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The panel body. It gets no padding from the shell, so whatever goes in brings its own — a ' +
        'padded head with a full-bleed divider under it is the shape every caller uses.',
    },
  ],

  tokens: [
    {
      token: '--caos-overlay-width',
      controls:
        'How wide the panel is: the max width of a centred modal (760px by default) and the fixed ' +
        'width of a drawer (420px). Set by the caller per dialog — a two-column record form needs ' +
        'more than a confirmation does.',
    },
    {
      token: '--caos-overlay-scrim',
      controls:
        'The dimming behind the panel. Defined on the element itself with a default of ' +
        'rgba(0, 0, 0, 0.6) — a token that names this component rather than a platform axis, which ' +
        'is recorded here rather than left buried in the stylesheet.',
    },
    { token: '--caos-color-surface', controls: 'The panel fill.' },
    { token: '--caos-color-border-strong', controls: 'The panel edge.' },
    { token: '--caos-shadow-float', controls: 'The one elevation reserved for pieces that float.' },
    { token: '--caos-radius-lg', controls: 'The corner radius of a centred modal. A drawer has none on its flush edge.' },
    { token: '--caos-space-4', controls: 'The gap between a centred modal and the edge of the screen.' },
  ],

  parts: [
    { part: 'scrim', is: 'the dimmed backdrop, which is also the click-to-close target' },
    { part: 'panel', is: 'the floating panel' },
  ],

  methods: [
    {
      name: 'requestClose',
      description:
        'Raise the close request from code, exactly as the scrim and Escape do — for a Cancel ' +
        'button inside the panel, so one path decides what closing means.',
      args: [],
    },
    {
      name: 'alertDialog',
      description:
        'Say something and wait for it to be acknowledged. One button, nothing to read back: the ' +
        'button, the scrim and Escape all mean the same thing. Takes { message, heading, ' +
        'confirmLabel }. This and the two below are CALLS against this one overlay rather than ' +
        'small dialog components of their own — they are the same dialog with different footers, ' +
        'so they share the scrim, the focus trap, the Escape behaviour and the dialog role instead ' +
        'of each keeping a copy.',
      args: [{ name: 'options', type: 'CaosDialogOptions', description: 'The message, and optionally a heading, the button words, and whether committing is destructive.' }],
    },
    {
      name: 'confirmDialog',
      description:
        'Ask before going ahead. Resolves true only if the committing button was the answer — the ' +
        'scrim and Escape are a no, because a dialog somebody dismissed is not a dialog somebody ' +
        'agreed to. Told that committing is destructive, the committing button takes the destructive colour and ' +
        'nothing else about the dialog changes.',
      args: [{ name: 'options', type: 'CaosDialogOptions', description: 'The question, and optionally a heading, the button words, and whether committing is destructive.' }],
    },
    {
      name: 'promptDialog',
      description:
        'Ask for something and read it back. Resolves the text as typed, or null if the dialog was ' +
        'cancelled or dismissed — which is how a caller tells "they typed nothing" from "they said ' +
        'no", two answers an empty string cannot keep apart. The field is focused first, because ' +
        'it is what the dialog is for.',
      args: [{ name: 'options', type: 'CaosPromptOptions', description: 'The message, plus the field’s own label, starting value and placeholder.' }],
    },
  ],

  placing:
    'Not placed on a page. The surface that opens the dialog creates the element and appends it — ' +
    'presence in the DOM is what open means, and removing it is what closing means. It positions ' +
    'itself against the viewport and covers the page, so it needs nothing around it; what it needs ' +
    'is a host that listens for the close and unmounts it, and content that brings its own padding.',

  whenToUse:
    'Something has to be dealt with in front of the page, rather than on it: a form for a new ' +
    'record, a confirmation, a picker, a detail panel that arrives from the side. Reach for this one ' +
    'whether it ends up centred or on the edge — it is the same shell either way.',

  insteadUse: [
    {
      name: 'Inspector',
      when:
        'The panel belongs BESIDE the work rather than over it, and the page behind it stays usable ' +
        'while somebody edits the properties of what they have selected.',
    },
    {
      name: 'SplitView',
      when: 'The second pane is part of the surface — a list on the left, the record you are reading on the right.',
    },
    {
      name: 'OverflowMenu',
      when: 'What opens is a short list of actions off a trigger, not a panel of content.',
    },
  ],

  accessibility:
    'The panel is a role="dialog" with aria-modal, named by the label attribute. On mount it moves ' +
    'focus inside (deferred a frame so slotted content has rendered its controls), traps Tab within ' +
    'the panel — piercing slotted custom elements, so a button whose real control lives in its own ' +
    'shadow root is still in the ring — and returns focus to whatever opened it when it is ' +
    'unmounted. The scrim is a real button with an accessible name, and Escape does the same thing ' +
    'it does. What is left to the author: set label, or the dialog has no name; and actually unmount ' +
    'on close, because the focus return happens on disconnect.',

  examples: [
    {
      key: 'modal',
      label: 'As a centred modal',
      summary:
        'The shell with its default placement: a dialog floating near the top of the page, scrim ' +
        'behind it, capped at the width its caller gives it.',
      placement: {
        component: 'caos-overlay',
        inputs: { label: 'Order details' },
        children: [
          {
            component: 'caos-inspector',
            inputs: { heading: 'ORD-4471' },
            children: [
              { component: 'caos-key-value-row', inputs: { label: 'Account', value: 'Meridian Supply' } },
              { component: 'caos-key-value-row', inputs: { label: 'Total', value: '$12,400' } },
            ],
          },
        ],
      },
      // It lays itself over the whole screen, so it is held in its own box and can be looked at
      // without taking the page over.
      stage: { contain: 300 },
      host: {
        summary:
          'sets the panel width, the way the create form and the package picker each set theirs, and ' +
          'unmounts the overlay when it reports a close — presence in the DOM is what open means',
        run: (stage) => {
          const overlay = stage.querySelector('caos-overlay');
          if (!overlay) return;
          // Narrower than the 760px default: this box is narrower than a screen, and a modal sized
          // for a screen would fill it edge to edge and hide the scrim that is half of what this
          // piece IS.
          overlay.style.setProperty('--caos-overlay-width', '420px');
          overlay.addEventListener('caos-overlay-close', () => overlay.remove());
        },
      },
    },
    {
      key: 'drawer',
      label: 'As a right-hand drawer',
      summary:
        'The same declaration with placement set to end: full height, flush against the right edge, ' +
        'no rounded corners on the side it is flush with — one input apart from the modal above.',
      placement: {
        component: 'caos-overlay',
        inputs: { label: 'Order details', placement: 'end' },
        children: [
          {
            component: 'caos-inspector',
            inputs: { heading: 'ORD-4471' },
            children: [
              { component: 'caos-key-value-row', inputs: { label: 'Account', value: 'Meridian Supply' } },
              { component: 'caos-key-value-row', inputs: { label: 'Total', value: '$12,400' } },
            ],
          },
        ],
      },
      stage: { contain: 300 },
      host: {
        summary: 'sets the drawer width and unmounts the overlay when it reports a close',
        run: (stage) => {
          const overlay = stage.querySelector('caos-overlay');
          if (!overlay) return;
          // 260px rather than the 420px default, for the same reason as above: a screen-sized drawer
          // fills a box this narrow and the scrim disappears with it.
          overlay.style.setProperty('--caos-overlay-width', '260px');
          overlay.addEventListener('caos-overlay-close', () => overlay.remove());
        },
      },
    },
  ],
};

export default docs;
